import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Empty, Tabs, Tag, Typography } from 'antd';

import {
  fetchAssessmentState,
  fetchPassedNews,
  PLAYING,
  selectors,
} from 'state';
import { LoadingScreen } from 'components';
import { assessmentCaptureScope } from '../../../state/app/assessment/captureRequests';

import NewsBuckets from './NewsBuckets';
import ConvictionSliders from './ConvictionSliders';
import RiskPanel from './RiskPanel';

import './assessment.css';
import '../NewsHistory2.css';

const { TabPane } = Tabs;

/** Player assessment surface: map the period's news onto instruments, set
 *  conviction sliders, watch soft-limit utilization. Rendered only when the
 *  session has an assessment attached (state/ returns a scenario). */
const Assessment: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const player = useSelector(selectors.player);
  const assessment = useSelector(selectors.assessment);
  const lastNews = useSelector(selectors.lastNews);

  // Hydrate on mount and re-sync on every socket tick: 'news_received' flips
  // news.last, 'passed_time' bumps elapsed_days. Also covers reconnects, which
  // land a fresh fetchPassedNews/session snapshot.
  useEffect(() => {
    if (!session?.id) return;
    dispatch(fetchAssessmentState({}));
  }, [
    dispatch,
    player.id,
    session?.id,
    session?.elapsed_days,
    session?.status,
    lastNews,
  ]);

  // Same news bootstrap guard as NewsHistory2.
  useEffect(() => {
    if (session?.news_fetched) return;
    if (!session?.id) return;
    dispatch(fetchPassedNews({ session_id: session.id }));
  }, [dispatch, session?.news_fetched, session?.id]);

  if (!assessment.fetched && assessment.fetching) return <LoadingScreen />;
  if (!assessment.attached) {
    return <Empty description="No assessment attached to this session." />;
  }

  // Writes 409 server-side outside PLAYING; mirror that in the UI.
  const canWrite =
    session.status === PLAYING &&
    (assessment.capture_scope === undefined ||
      assessment.capture_scope ===
        assessmentCaptureScope(session.id, player.id, session.elapsed_days));
  const deferred =
    assessment.capture_only === true || assessment.status === 'deferred';

  return (
    <div className="assessment-root">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          Assessment
        </Typography.Title>
        {!deferred && <Tag>{assessment.scenario}</Tag>}
        {assessment.current_period !== null &&
          assessment.current_period !== undefined && (
            <Tag color="blue">Period {assessment.current_period}</Tag>
          )}
        {session.status !== PLAYING && (
          <Tag color="orange">Read-only — session not running</Tag>
        )}
      </div>
      {deferred && (
        <Typography.Paragraph type="secondary">
          Your responses are saved. Results pending review.
        </Typography.Paragraph>
      )}
      <Tabs size="small" animated={false}>
        <TabPane tab="News Mapping" key="mapping">
          <NewsBuckets canWrite={canWrite} />
        </TabPane>
        <TabPane tab="Convictions" key="convictions">
          <ConvictionSliders canWrite={canWrite} />
        </TabPane>
        <TabPane tab="Risk" key="risk">
          {deferred ? (
            <Typography.Paragraph type="secondary">
              Use the Markets view to review your positions and trading limits.
            </Typography.Paragraph>
          ) : (
            <RiskPanel />
          )}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Assessment;
